import lodash from 'lodash'
import Raven from 'raven-js'
import * as Sentry2 from '@sentry/browser'
import { LoggableError } from '../../../shared/lib/ClientLog.no-deps'
import { shouldUseSentry } from '../../../shared/lib/sentry-helpers'
import { ensureError } from '../../../shared/lib/functional/error.no-deps'
import { Logg_event } from '../../zap/Logg/Logg.event'
import { Plat_isCordova } from '../../zap/Plat/Plat.device'

export function register(token: string, version: string) {
    Raven.config(token, {
        release: process.env.RELEASE,
        tags: { version },
        ignoreErrors: [
            /^DatabaseClosedError Database has been closed$/,
            /^Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.$/,
        ],
        dataCallback(payload) {
            if (Plat_isCordova()) {
                const normalize = (filename: string) => filename.split('/').pop()

                if (!lodash.isUndefined(payload.exception)) {
                    payload.exception.values[0].stacktrace.frames.forEach((frame: { filename: string }) => {
                        frame.filename = '/' + normalize(frame.filename)
                    })

                    payload.culprit = payload.exception.values[0].stacktrace.frames[0].filename
                } else {
                    if (!lodash.isUndefined(payload.culprit)) {
                        payload.culprit = '/' + normalize(payload.culprit)
                    }
                }
            }
            return payload
        },
    }).install()
}

export function userSet(id: number, email: string) {
    Raven.setUserContext({
        id: id.toString(),
        email,
    })
    Sentry2.setUser({
        id: id.toString(),
        email,
    })
}

export function customerSet(customerId: number, customerName: string) {
    Raven.setTagsContext({
        customerId,
        customerName,
    })
    Sentry2.setTags({
        customerId,
        customerName,
    })
}

export function logout() {
    Raven.setUserContext({ id: null, email: null })
    Raven.setTagsContext({ customerId: null, customerName: null })
}

export type TOpts = {
    tags?: { [key: string]: any }
    extra?: { [key: string]: any }
}

export function err(error: Error | string, opts: TOpts) {
    try {
        error = ensureError(error)

        if (error instanceof LoggableError) {
            opts.extra = { ...opts.extra, ...error.extra }
            opts.tags = { ...opts.tags, ...error.tags }
        }

        sendToSentry(error, opts)

        /* eslint-disable-next-line no-console */
        console.error(error, opts)

        Logg_event('SentryError', { message: error.message })
    } catch (newError) {
        /* eslint-disable-next-line no-console */
        console.error(newError)
        sendToSentry(newError, { extra: { context: 'sentry.err failed' } })
    }
}

export function simpleError(error: Error | string) {
    err(error, {})
}

export function sendToSentry(error: Error, options?: Raven.RavenOptions) {
    if (!ENV.isProduction) {
        return
    }
    if (shouldUseSentry()) {
        Raven.captureException(error, options)
    }
}

declare global {
    interface Window {
        onunhandledrejection: (event: PromiseRejectionEvent) => void
    }
}

window.onunhandledrejection = function (evt: PromiseRejectionEvent) {
    simpleError(evt.reason)
}

window.onerror = function (_, __, ___, ____, error) {
    simpleError(error)
}

/** Using a different sentry client to cause replay events for different sentry "app" */
export function triggerSentryReplay() {
    Sentry2.captureException(new Error('trigger-replay'))
}
